/**
 * The POST every editing tool makes: signed in, a 401 reported to the shell,
 * and a failure turned into one German sentence for the editor — the server's
 * `detail` when it sent one, otherwise the page's own message.
 *
 * The pages used to put `HTTP 500`, "Implementation failed" or the browser's
 * "Failed to fetch" into their toasts, and Find Similar printed the server's
 * message twice ("Die Links konnten nicht eingefügt werden: Die Links …").
 */

import { errorDetail } from '../../utils/download'
import { reportSessionExpired } from '../../utils/session'
import { toast } from '../../utils/toast'

/** A failure the editor may read: its message is German, from the server or the page. */
export class ApiError extends Error {}

/** A 401. The shell's banner says so; the page adds nothing. */
export class SessionExpiredError extends Error {}

export async function postRequest(url: string, body: unknown, token: string | null, fallback: string): Promise<Response> {
    let response: Response
    try {
        response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify(body),
        })
    } catch (err) {
        console.error('Request failed', url, err)
        throw new ApiError(fallback)
    }
    if (response.status === 401) {
        reportSessionExpired()
        throw new SessionExpiredError('Die Anmeldung ist abgelaufen.')
    }
    if (!response.ok) throw new ApiError((await errorDetail(response)) ?? fallback)
    return response
}

export async function postJson<T>(url: string, body: unknown, token: string | null, fallback: string): Promise<T> {
    const response = await postRequest(url, body, token, fallback)
    try {
        return (await response.json()) as T
    } catch {
        throw new ApiError(fallback)
    }
}

/** What to tell the editor about `err`: null after a 401, which the shell already reports. */
export function failureMessage(err: unknown, fallback: string): string | null {
    if (err instanceof SessionExpiredError) return null
    if (err instanceof ApiError) return err.message
    console.error(err)
    return fallback
}

/** Raise the failure toast for `err`, unless it was a 401. */
export function reportFailure(err: unknown, fallback: string): void {
    const message = failureMessage(err, fallback)
    if (message) toast.error(message)
}

export const AI_EDIT_FAILED = 'Die Änderung konnte nicht erzeugt werden. Bitte noch einmal versuchen.'

/**
 * „Mit KI bearbeiten“ in the headline tools, Revenue News and the Article
 * Generator: `passage` rewritten by the editor's `instruction`, the whole
 * field around it as context (`/api/edit-with-gpt`).
 */
export async function rewritePassage(
    fullText: string,
    passage: string,
    instruction: string,
    token: string | null,
): Promise<string> {
    const data = await postJson<{ edited_text?: string | null }>(
        '/api/edit-with-gpt',
        { full_text: fullText, selected_text: passage, instruction },
        token,
        AI_EDIT_FAILED,
    )
    return data.edited_text ?? ''
}
